import React from "react";
import { defaultColors } from "@/themes/antd-theme";
import { useTheme } from "@/App";
import { useTranslation } from "react-i18next";

// Flat white/dark card with a thick colored top accent border and a
// circular icon badge — same KPI design used across every page that renders
// StatCard (dashboards, config pages, reports). Kept the original prop
// signature (icon, label, value, bgColor, iconColor) so this is a drop-in
// visual change with no call-site updates needed.
//
// Optional extras (a call that passes none of them renders exactly as before):
//   valueExtra  node beside the value (e.g. "/ 12")      valueSize  value font size in px
//   compact     tighter padding, a smaller label         className / style  merged onto the card
//   data-*, aria-*, role, tabIndex, onKeyDown are passed to the card (so a tile can be a keyboard-operable toggle);
//   any other prop a caller sends is ignored, as before.
const StatCard = ({
  icon,
  label,
  value,
  bgColor,
  iconColor,
  onClick,
  sub,
  valueExtra,
  valueSize,
  compact = false,
  className = "",
  style,
  ...rest
}) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const finalBgColor =
    bgColor || (isDark
      ? defaultColors.palette.teal[900]
      : defaultColors.palette.teal[50]);

  const finalIconColor = iconColor || defaultColors.primary;

  const cardBgColor = isDark
    ? defaultColors.palette.gray[900]
    : "#ffffff";

  const borderColor = isDark ? defaultColors.palette.gray[700] : "#e5e7eb";

  const passThrough = Object.fromEntries(
    Object.entries(rest).filter(([k]) => k.startsWith("data-") || k.startsWith("aria-") || ["role", "tabIndex", "onKeyDown"].includes(k))
  );
  return (
    <div
      {...passThrough}
      className={`flex-1 min-w-0 rounded-xl px-4 py-3 transition-all duration-200 cursor-pointer hover:-translate-y-0.5 ${className}`.trim()}
      onClick={onClick}
      style={{
        backgroundColor: cardBgColor,
        border: `1px solid ${borderColor}`,
        borderTop: `${compact ? 3 : 4}px solid ${finalIconColor}`,
        ...(compact ? { padding: "8px 14px 10px" } : {}),
        boxShadow: isDark
          ? "0 1px 3px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)"
          : "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = isDark
          ? "0 8px 16px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.35)"
          : `0 10px 20px -4px ${finalIconColor}26, 0 4px 8px rgba(0,0,0,0.06)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = isDark
          ? "0 1px 3px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)"
          : "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)";
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p
            className="text-base font-medium m-0 truncate"
            style={{ color: isDark ? "#9ca3af" : "#6b7280", ...(compact ? { fontSize: 13 } : {}) }}
          >
            {t(label)}
          </p>
          {valueExtra ? (
            <div className="flex items-baseline gap-1 flex-wrap">
              <p className="text-xl font-bold m-0" style={{ color: finalIconColor, ...(valueSize ? { fontSize: valueSize, lineHeight: 1.1 } : {}) }}>
                {value}
              </p>
              {valueExtra}
            </div>
          ) : (
            <p className="text-xl font-bold m-0" style={{ color: finalIconColor, ...(valueSize ? { fontSize: valueSize, lineHeight: 1.1 } : {}) }}>
              {value}
            </p>
          )}
          {sub ? (
            <p className="text-xs m-0 truncate" style={{ color: isDark ? "#9ca3af" : "#6b7280" }} title={typeof sub === 'string' ? sub : undefined}>
              {sub}
            </p>
          ) : null}
        </div>
        {icon ? (
        <div
          className="flex items-center justify-center rounded-full flex-shrink-0"
          style={{
            backgroundColor: finalBgColor,
            width: 40,
            height: 40,
            boxShadow: `inset 0 0 0 1px ${finalIconColor}22`,
          }}
        >
          {React.cloneElement(icon, {
            size: 20,
            color: finalIconColor,
          })}
        </div>
        ) : null}
      </div>
    </div>
  );
};

export default StatCard;
